import { FC, ReactNode, useState } from 'react';
import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from '@mui/material';

type TPgConfirmDialogProps = {
	open: boolean;
	title: string;
	/** Explanation of what happens; a string or any content. */
	description?: ReactNode;
	/** Extra content (fields) between the description and the reason. */
	children?: ReactNode;
	/** Asks for a reason (mandatory, at least 3 characters); `onConfirm` receives it. */
	reasonLabel?: string;
	reasonRequired?: boolean;
	confirmLabel: string;
	confirmColor?: 'primary' | 'error' | 'warning';
	/** Disables the confirm button for reasons the dialog does not know (invalid fields). */
	confirmDisabled?: boolean;
	busy?: boolean;
	/** Error of the last attempt, shown inside the dialog. */
	error?: string | null;
	onConfirm: (reason: string) => void;
	onClose: () => void;
	maxWidth?: 'xs' | 'sm' | 'md';
};

const MIN_REASON_LENGTH = 3;

/** Confirmation with an optional mandatory reason; the dialog never closes itself. */
export const PgConfirmDialog: FC<TPgConfirmDialogProps> = ({
	open,
	title,
	description,
	children,
	reasonLabel,
	reasonRequired = true,
	confirmLabel,
	confirmColor = 'primary',
	confirmDisabled = false,
	busy = false,
	error,
	onConfirm,
	onClose,
	maxWidth = 'sm',
}) => {
	const [reason, setReason] = useState('');

	// Every opening starts with an empty reason.
	const [wasOpen, setWasOpen] = useState(open);
	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) setReason('');
	}

	const reasonInvalid = !!reasonLabel && reasonRequired && reason.trim().length < MIN_REASON_LENGTH;

	return (
		<Dialog open={open} onClose={busy ? undefined : onClose} maxWidth={maxWidth} fullWidth>
			<DialogTitle color="primary">{title}</DialogTitle>
			<DialogContent>
				{typeof description === 'string' ? (
					<Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
						{description}
					</Typography>
				) : (
					description
				)}
				{children}
				{reasonLabel && (
					<TextField
						label={reasonLabel}
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						fullWidth
						multiline
						minRows={2}
						size="small"
						required={reasonRequired}
						sx={{ mt: 1 }}
						disabled={busy}
					/>
				)}
				{error && (
					<Alert severity="error" sx={{ mt: 2 }}>
						{error}
					</Alert>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={busy}>
					Cancel
				</Button>
				<Button
					variant="contained"
					color={confirmColor}
					onClick={() => onConfirm(reason.trim())}
					disabled={busy || reasonInvalid || confirmDisabled}
					startIcon={busy ? <CircularProgress size={16} /> : undefined}
				>
					{confirmLabel}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
